/* eslint-disable @typescript-eslint/naming-convention */
import { ProductThumbnailType6 } from "@/components/commons/productThumbnailType6"
import { useAllCollarsQuery } from "@/type-and-hooks"
import { Box, Button, Flex, Text, useMediaQuery } from "@chakra-ui/react"
import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { breadCrumbFirstLevel, breadCrumbProductsSecondLevel, breadCrumbSealantThirdLevel, mobileBreakpoint } from "../common/commonConstants"
import { SimpleGridWrapper, SubPage, SubPageContent } from "../components/SubPage"
import { BreadCrumb } from "../components/commons/breadcrumb"

// Subcategory descriptions data
const subcategoryDescriptions = [
  {
    pattern: /fire\s*rated/i,
    text1: "Fire-rated sealants provide tested and certified fire protection for penetrations.",
    text2: "Suitable for maintaining fire integrity in critical installations."
  },
  {
    pattern: /acoustic/i,
    text1: "Acoustic sealants provide both fire and sound insulation properties.",
    text2: "Ideal for installations requiring both fire protection and noise reduction."
  },
  {
    pattern: /intumescent/i,
    text1: "Intumescent sealants expand when exposed to heat, creating a fire-resistant barrier.",
    text2: "Designed to seal gaps and prevent fire spread in high-temperature conditions."
  },
  {
    pattern: /silicone/i,
    text1: "Silicone-based sealants offer flexible, durable fire protection.",
    text2: "Provides excellent adhesion and weather resistance for various applications."
  }
]

// Function to get description based on subcategory
const getSubcategoryDescription = (subcategory: string): { text1: string; text2: string } => {
  const match = subcategoryDescriptions.find(item => subcategory.match(item.pattern))

  return match
    ? { text1: match.text1, text2: match.text2 }
    : {
        text1: "Professional fire-resistant sealant products for fire collar systems.",
        text2: "Designed to meet fire safety standards and provide reliable protection."
      }
}

export const SealantSubPage = (): JSX.Element => {
  const location = useLocation()
  const [pathName, setPathName] = useState<string>("")
  const navigate = useNavigate()
  const [isMobile] = useMediaQuery(`(max-width:${mobileBreakpoint}px)`)
  const columnCount = isMobile ? 1 : 3
  console.log(isMobile, columnCount)

  const subCategory = pathName.toLowerCase().replaceAll(" / ", "-").replaceAll(" - ", "-").replaceAll(" ", "-")

  useEffect(() => {
    if (location) {
      const tmp = location.pathname.slice(location.pathname.lastIndexOf("/") + 1, location.pathname.length)
      setPathName(tmp.toUpperCase().replaceAll("_", "/").replaceAll("-", " "))
    }
  }, [location])

  const { loading, data } = useAllCollarsQuery({ variables: { collarTypeId: subCategory }, fetchPolicy: "cache-and-network" })

  // Get description for current subcategory
  const description = getSubcategoryDescription(pathName)

  // Get product count
  const productCount = data?.allCollars?.length ?? 0

  return (
    <SubPage>
      <BreadCrumb
        options={{
          ...breadCrumbFirstLevel,
          ...breadCrumbProductsSecondLevel,
          ...breadCrumbSealantThirdLevel,
          fourthLevel: { title: pathName }
        }}
      />
      <SubPageContent>
        {/* Description Section */}
        <Box pt="4px" pb="16px" mb="20px" textAlign={"center"}>
          <Text fontSize="16px" lineHeight="24px" mb="8px">
            {description.text1}
          </Text>
          <Text fontSize="16px" lineHeight="24px" mb="24px">
            {description.text2}
          </Text>
          <Button variant="Button1Regular" onClick={() => navigate("/distributors")}>
            Buy From Distributor
          </Button>
        </Box>

        {/* Product Count (Left) and Instructions (Right) */}
        <Flex justify="space-between" align="flex-start" mb="4px">
          {/* Product Count - Left Side */}
          <Text fontSize="12px" color="gray.400" fontWeight="400">
            {productCount} items
          </Text>
        </Flex>

        {/* Products Grid */}
        <Flex overflow={"hidden"} mb="8px" justify={"center"}>
          <SimpleGridWrapper props={{ width: "100%", padding: "0 16px" }}>
            {data?.allCollars?.map((item, index) => (
              <React.Fragment key={`${index}_allSealants`}>
                <ProductThumbnailType6 prodCode={item?.prod_code} prodPhoto={item?.prod_photo} />
              </React.Fragment>
            ))}
          </SimpleGridWrapper>
        </Flex>
      </SubPageContent>
    </SubPage>
  )
}
